import React from 'react';
import './EditableTable.css';
import IconWarning from 'src/assets/images/icons/warning-2-red.svg';
import IconClose from 'src/assets/images/icons/close-square.svg';
import { ValidateInvoiceStep } from '../../molecules/uploadInvoice/types';

export interface EditableTableColumn {
  key: string;
  label: string;
  width?: number;
  type?: string;
  isEditable?: boolean;
}

export interface EditableTableGroup {
  key?: string;
  label: string;
  rows: any[];
  expanded: boolean;
  onToggle: () => void;
  onAdd: () => void;
  isHideAddButton?: boolean;
}

interface EditableTableProps {
  columns: EditableTableColumn[];
  groups?: EditableTableGroup[];
  onDelete: (groupKey: string, rowIdx: number) => void;
  onCellEdit: (groupKey: string, rowIdx: number, field: string, value: any) => void;
  onBlur?: (groupKey: string, rowIdx: number, field: string, value: any) => void;
  activeStep?: ValidateInvoiceStep;
}

const EMPTY_PLACEHOLDER = '{empty}';

const isFirstStep = (step: ValidateInvoiceStep) => step === ValidateInvoiceStep.Step1_EditOCRData;

const getBorderBottomStyle = (step: ValidateInvoiceStep, error: any) => {
  const firstStepBorderColor = error ? '1px solid #E72547' : '1px solid #e0e0e0';
  return isFirstStep(step) ? firstStepBorderColor : 'none';
};

const getDisplayValue = (step: ValidateInvoiceStep, updatedValue: any, updatedError: any) => {
  if ((updatedValue || updatedValue === 0) && updatedValue !== '') return updatedValue;
  if (!isFirstStep(step) && updatedError) return EMPTY_PLACEHOLDER;
  return '';
};

const shouldShowHelper = (step: ValidateInvoiceStep, error: any, helperText: any, updatedValue: any, originalValue: any) => {
  if (isFirstStep(step)) return error && helperText;
  if (updatedValue !== originalValue) {
    return true;
  }
  return false;
};

const getHelperColor = (step: ValidateInvoiceStep, error: any) => (!isFirstStep(step) && !error ? '#808080' : '#E72547');

const getHelperIcon = (step: ValidateInvoiceStep, error: any) => (!isFirstStep(step) && !error ? IconClose : IconWarning);

const EditableTableCell: React.FC<{
  col: EditableTableColumn;
  row: any;
  idx: number;
  groupKey: string | undefined;
  step: ValidateInvoiceStep;
  onCellEdit: (groupKey: string, rowIdx: number, field: string, value: any) => void;
  onBlur?: (groupKey: string, rowIdx: number, field: string, value: any) => void;
  inputClassName: string;
  cellClassName: string;
}> = ({ col, row, idx, groupKey, step, onCellEdit, onBlur, inputClassName, cellClassName }) => {
  const error = row[`${col.key}__error`];
  const helperText = row[`${col.key}__helperText`];
  const updatedError = row[`${col.key}__updatedError`];
  const originalValue = row[`${col.key}__originalValue`];
  const updatedValue = row[col.key];
  const borderBottomStyle = getBorderBottomStyle(step, error);
  const displayValue = getDisplayValue(step, updatedValue, updatedError);
  const isEmptyPlaceholder = displayValue === EMPTY_PLACEHOLDER;
  const showHelper = shouldShowHelper(step, error, helperText, updatedValue, originalValue);
  return (
    <td key={col.key} className={cellClassName} style={{ width: col.width, border: groupKey || groupKey === '' ? 'none' : undefined }}>
      <input
        type={col.type ?? 'text'}
        className={inputClassName}
        value={displayValue}
        onChange={(e) => onCellEdit(groupKey ?? '', idx, col.key, e.target.value)}
        onBlur={onBlur ? (e) => onBlur(groupKey ?? '', idx, col.key, e.target.value) : undefined}
        style={{
          borderBottom: borderBottomStyle,
          color: !isFirstStep(step) && updatedError ? '#E72547' : undefined,
          fontStyle: isEmptyPlaceholder ? 'italic' : undefined,
        }}
        disabled={!isFirstStep(step) || col.isEditable === false}
      />
      {showHelper && (
        <div style={{ color: getHelperColor(step, error), fontSize: 9, marginTop: 2, marginLeft: 2 }}>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src={getHelperIcon(step, error)}
              alt='warning icon'
              style={{ height: 12, width: 12, marginRight: 4, verticalAlign: 'middle', marginLeft: 4 }}
            />
            {isFirstStep(step) ? helperText : originalValue}
          </span>
        </div>
      )}
    </td>
  );
};

export const EditableTable: React.FC<EditableTableProps> = ({ columns, groups, onDelete, onCellEdit, onBlur, activeStep }) => {
  const inputClassName = 'editable-input';
  const rowClassName = 'editable-row';
  const cellClassName = 'editable-cell';
  const addLabel = 'Add New';
  const isUngrouped = Array.isArray(groups) && groups.length === 1 && !(groups[0].key || groups[0].key === '');
  const ungroupedRows = isUngrouped ? groups[0].rows : [];

  // Generate stable key from row data to avoid using array index
  const getRowKey = (row: any, idx: number, groupKey?: string) => {
    // Try to use unique identifiers from the row if available
    const uniqueId = row.id || row.shipmentNumber || row.accountNumber || row.chargeName;
    if (uniqueId) {
      return groupKey ? `${groupKey}-${uniqueId}` : String(uniqueId);
    }
    // Fallback: create a hash from row data
    const rowString = JSON.stringify(row);
    return `${groupKey || 'row'}-${rowString.substring(0, 50)}-${idx}`;
  };
  return (
    <div className='editable-table-container'>
      <table className='editable-table'>
        <thead>
          <tr>
            <th className='action-column' aria-label='Actions' />
            {columns.map((col) => (
              <th key={col.key} style={{ width: col.width }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isUngrouped ? (
            <>
              {ungroupedRows.map((row, idx) => (
                <tr key={getRowKey(row, idx)}>
                  <td className='action-cell'>
                    <button type='button' className='delete-button' onClick={() => onDelete('', idx)} title='Delete' disabled={!isFirstStep(activeStep!)}>
                      <span style={{ fontWeight: 'bold', color: '#d32f2f' }}>×</span>
                    </button>
                  </td>
                  {columns.map((col) => (
                    <EditableTableCell
                      key={col.key}
                      col={col}
                      row={row}
                      idx={idx}
                      groupKey=''
                      step={activeStep!}
                      onCellEdit={onCellEdit}
                      onBlur={onBlur}
                      inputClassName={inputClassName}
                      cellClassName={cellClassName}
                    />
                  ))}
                </tr>
              ))}
              <tr>
                <td colSpan={columns.length + 1} style={{ textAlign: 'center', border: '1px solid #e0e0e0' }} className='h-40'>
                  {!groups[0].isHideAddButton && (
                    <button
                      className="inline-flex items-center cursor-pointer text-[14px] font-['Roboto'] text-[#00992B]"
                      onClick={groups[0].onAdd}
                      tabIndex={0}
                      type='button'
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          groups[0].onAdd();
                        }
                      }}
                      style={{ fontFamily: 'Roboto, Arial, sans-serif', fontSize: 14, color: '#00992B' }}
                      disabled={!isFirstStep(activeStep!)}
                    >
                      <span className='ml-1' style={{ marginRight: 8 }}>
                        {addLabel}
                      </span>
                      <span className='add-icon mr-2 text-[20px] font-bold'>+</span>
                    </button>
                  )}
                </td>
              </tr>
            </>
          ) : (
            groups.map((group) => (
              <React.Fragment key={group.key}>
                <tr className='group-header'>
                  <td colSpan={columns.length + 1} className='h-40'>
                    <div className='group-header-content'>
                      <button className='group-title' onClick={group.onToggle} type='button'>
                        <span className='expand-icon'>{group.expanded ? '\u25bc' : '\u25b6'}</span>
                        <span className='group-label'>{group.label}</span>
                      </button>
                      {!group.isHideAddButton && (
                        <button
                          className="inline-flex items-center cursor-pointer text-[14px] font-['Roboto'] text-[#00992B]"
                          onClick={group.onAdd}
                          tabIndex={0}
                          type='button'
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              group.onAdd();
                            }
                          }}
                          style={{ fontFamily: 'Roboto, Arial, sans-serif', fontSize: 14, color: '#00992B' }}
                          disabled={!isFirstStep(activeStep!)}
                        >
                          <span className='ml-1' style={{ marginRight: 8 }}>
                            {addLabel}
                          </span>
                          <span className='add-icon mr-2 text-[20px] font-bold'>+</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                <tr className={`group-content ${group.expanded ? 'expanded' : 'collapsed'}`}>
                  <td colSpan={columns.length + 1}>
                    <div className='group-rows'>
                      {group.rows.map((row, idx) => (
                        <div key={getRowKey(row, idx, group.key)} className={rowClassName}>
                          <div className='action-cell'>
                            <button
                              type='button'
                              className='delete-button'
                              onClick={() => onDelete(group.key, idx)}
                              title='Delete'
                              disabled={!isFirstStep(activeStep!)}
                            >
                              <span style={{ fontWeight: 'bold', color: '#d32f2f' }}>×</span>
                            </button>
                          </div>
                          {columns.map((col) => (
                            <EditableTableCell
                              key={col.key}
                              col={col}
                              row={row}
                              idx={idx}
                              groupKey={group.key}
                              step={activeStep!}
                              onCellEdit={onCellEdit}
                              onBlur={onBlur}
                              inputClassName={inputClassName}
                              cellClassName={cellClassName}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
